import * as React from "react"
import { useState } from "react"
import { Box, Checkbox, FormControlLabel, IconButton, Popover, TextField, Tooltip, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import {
    recentDataDateFrom,
    recentDataDateTo,
    recentDataDays,
    recentDataEnabled,
} from "../@signals"

const DEFAULT_DAYS = 30


function formatLabel(days, from, to) {
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : null
    if (from && to) return `Seen: ${formatDate(from)} - ${formatDate(to)}`
    if (from) return `Seen: from ${formatDate(from)}`
    if (to) return `Seen: until ${formatDate(to)}`
    return `Seen in the last ${days}d`
}


/**
 * The rolling window, lifted from Products Review with its four signals.
 *
 * It decides what the coverage percentages are *about*, not how fast they are:
 * a site holds 198.537 products but only 48.666 seen in the last 30 days, and
 * the other 150.000 are delisted — counting them drags every number down for
 * reasons that have nothing to do with the scraper's health.
 */
export default function ScrapingDataReviewRecentDataFilter() {
    const [anchor, setAnchor] = useState(null)

    const hasCustomDates = recentDataDateFrom.value || recentDataDateTo.value
    const hasCustomDays = recentDataDays.value !== DEFAULT_DAYS

    const handleReset = () => {
        recentDataDays.value = DEFAULT_DAYS
        recentDataDateFrom.value = null
        recentDataDateTo.value = null
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={recentDataEnabled.value}
                        onChange={(e) => recentDataEnabled.value = e.target.checked}
                        size="small"
                    />
                }
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconSeeker icon="clock-rotate-left" sx={{ fontSize: 14, color: '#10b981' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatLabel(recentDataDays.value, recentDataDateFrom.value, recentDataDateTo.value)}
                        </Typography>
                    </Box>
                }
                sx={{ m: 0 }}
            />
            <Tooltip title="Configure the window">
                <IconButton
                    size="small"
                    onClick={(e) => setAnchor(e.currentTarget)}
                    sx={{
                        ml: 0.5,
                        color: (hasCustomDates || hasCustomDays) ? '#10b981' : '#9ca3af',
                        '&:hover': { color: '#10b981' }
                    }}
                >
                    <IconSeeker icon="sliders" sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
                    <Typography variant="subtitle2" sx={{ color: '#10b981', mb: 0.5 }}>
                        Products seen in the last…
                    </Typography>
                    <TextField
                        label="Days"
                        type="number"
                        size="small"
                        fullWidth
                        value={recentDataDays.value}
                        onChange={(e) => {
                            const val = parseInt(e.target.value, 10)
                            if (val > 0) recentDataDays.value = val
                        }}
                        InputProps={{ inputProps: { min: 1 } }}
                        disabled={hasCustomDates}
                        helperText={hasCustomDates ? "Using custom date range" : "By the product's last capture date"}
                    />
                    <Typography variant="caption" sx={{ color: '#9ca3af', textAlign: 'center' }}>
                        — or use custom dates —
                    </Typography>
                    <TextField
                        label="From"
                        type="date"
                        size="small"
                        fullWidth
                        value={recentDataDateFrom.value || ''}
                        onChange={(e) => recentDataDateFrom.value = e.target.value || null}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="To"
                        type="date"
                        size="small"
                        fullWidth
                        value={recentDataDateTo.value || ''}
                        onChange={(e) => recentDataDateTo.value = e.target.value || null}
                        InputLabelProps={{ shrink: true }}
                    />
                    {!recentDataEnabled.value && (
                        <Typography variant="caption" sx={{ color: '#b45309' }}>
                            Window off: delisted products are counted too, so every coverage
                            percentage drops for reasons unrelated to the scraper.
                        </Typography>
                    )}
                    {(hasCustomDates || hasCustomDays) && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Typography
                                variant="caption"
                                sx={{ color: '#10b981', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                onClick={handleReset}
                            >
                                Reset to default ({DEFAULT_DAYS}d)
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Popover>
        </Box>
    )
}
